import { Controller } from "@hotwired/stimulus"

// Live EK→VK preview inside the calculation drawer. Posts the form's current
// (unsaved) state to the preview endpoint and swaps the returned summary fragment
// in — the endpoint never persists anything, and all math stays server-side.
const DEBOUNCE_MS = 500

export default class extends Controller {
  static targets = ["summary"]
  static values = { url: String }

  connect() {
    // The summary sits inside the form; a data-action on the form could not reach
    // this controller (Stimulus resolves actions upwards, not into descendants).
    this.form = this.element.closest("form") || this.element
    this.schedule = this.schedule.bind(this)
    this.form.addEventListener("input", this.schedule)
    this.form.addEventListener("change", this.schedule)
  }

  disconnect() {
    this.form.removeEventListener("input", this.schedule)
    this.form.removeEventListener("change", this.schedule)
    clearTimeout(this.timeout)
    this.abortInFlight()
  }

  schedule() {
    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => this.refresh(), DEBOUNCE_MS)
  }

  async refresh() {
    if (!this.hasSummaryTarget) return

    const body = new FormData(this.form)
    // The form carries _method=patch for the save route; Rack's method override would
    // rewrite this POST into a PATCH and miss the preview route. Drop it.
    body.delete("_method")

    // Most events re-emit the same values; rendering costs a full calculation pass,
    // so only ask the server when something actually moved.
    const signature = [...body].map(([name, value]) => `${name}=${value}`).join("\n")
    if (signature === this.lastSignature) return

    this.abortInFlight()
    this.controller = new AbortController()

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        body,
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          Accept: "text/html",
        },
        signal: this.controller.signal,
      })

      if (!response.ok) throw new Error(`Preview failed: ${response.status}`)

      this.summaryTarget.innerHTML = await response.text()
      this.distributeRowCosts()
      // Recorded only on success so a failed render is retried by the next edit.
      this.lastSignature = signature
    } catch (error) {
      if (error.name === "AbortError") return
      // Keep the last good summary — slightly stale beats blank.
    }
  }

  // The fragment carries every row's cost per unit (Costing::RowCosts) keyed by the row's
  // child index; each row has a matching data-costing-row-cost slot.
  distributeRowCosts() {
    const source = this.summaryTarget.querySelector("[data-costing-row-costs]")
    if (!source) return

    const costs = JSON.parse(source.dataset.costingRowCosts)
    this.form.querySelectorAll("[data-costing-row-cost]").forEach((slot) => {
      const label = costs[slot.dataset.costingRowCost]
      if (label !== undefined) slot.textContent = label
    })
  }

  abortInFlight() {
    this.controller?.abort()
    this.controller = null
  }
}
